import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Normalize API `business` on a service (string, `{ _id }`, ObjectId, EJSON `{ $oid }`) to a Mongo id string. */
export function coerceBusinessIdRef(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) return value
  if (value && typeof value === "object") {
    const v = value as { _id?: unknown; $oid?: unknown }
    if (typeof v._id === "string" && v._id.length > 0) return v._id
    if (typeof v.$oid === "string" && v.$oid.length > 0) return v.$oid
    if (v._id != null && typeof (v._id as { toString?: () => string }).toString === "function") {
      const s = (v._id as { toString: () => string }).toString()
      if (s && s !== "[object Object]") return s
    }
  }
  return undefined
}

/** Returns a URL only when `image` is a non-empty http(s) URL; otherwise omit from API payloads. */
export function optionalServiceImageUrl(image: string | undefined): string | undefined {
  const trimmed = image?.trim()
  if (!trimmed) return undefined
  try {
    const parsed = new URL(trimmed)
    if (parsed.protocol === "http:" || parsed.protocol === "https:") {
      return trimmed
    }
  } catch {
    /* not a valid URL */
  }
  return undefined
}

export function formatAxiosErrorMessage(
  error: unknown,
  fallback: string
): string {
  const err = error as { response?: { data?: unknown }; message?: string }
  const data = err?.response?.data

  if (typeof data === "string" && data.trim()) {
    return data.trim()
  }

  if (data && typeof data === "object") {
    const record = data as Record<string, unknown>
    const message = record.message
    if (Array.isArray(message)) {
      return message.filter(Boolean).join(", ")
    }
    if (typeof message === "string" && message.length > 0) {
      return message
    }
    if (typeof record.msg === "string" && record.msg.length > 0) {
      return record.msg
    }
  }

  const axiosMessage = err?.message
  if (
    typeof axiosMessage === "string" &&
    axiosMessage.length > 0 &&
    !/^Request failed with status code \d+$/.test(axiosMessage)
  ) {
    if (axiosMessage === "Network Error" && !err?.response) {
      return "Network error — check your connection. If you use an ad blocker, allow localhost or disable it for this site (ad API paths are often blocked)."
    }
    return axiosMessage
  }

  return fallback
}
